import { Inject, Injectable } from "@angular/core";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService, MsalBroadcastService } from "@azure/msal-angular";
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, InteractionStatus, InteractionType } from "@azure/msal-browser";

import { BehaviorSubject, catchError, filter, from, map, Observable, switchMap, tap } from "rxjs";
import { UserService } from "../services/user.service";
import { User } from "../core";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser: Observable<User | null>;
  private idToken?: string;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService
  ) {
    this.currentUser = this.currentUserSubject.asObservable();
  }

  getToken(): string | undefined {
    return this.idToken;
  }

  setLoggedUser(): Observable<InteractionStatus> {
    // Ensure any pending redirect response is processed before we attempt silent token acquisition.
    // If we skip this, the app can get stuck in a redirect loop because no account is available yet.
    return this.msalService.handleRedirectObservable().pipe(
      tap(result => {
        if (result?.account) {
          this.msalService.instance.setActiveAccount(result.account);
        }
      }),
      switchMap(() => this.ensureAuthenticated()),
      tap(result => {
        if (result?.account) {
          this.userService.setLoggedUser(result.account);
          this.idToken = result.idToken;
        } else {
          this.userService.setLoggedUser();
          this.idToken = undefined;
        }
      }),
      switchMap(() => this.msalBroadcastService.inProgress$.pipe(filter((status: InteractionStatus) => status === InteractionStatus.None)))
    );
  }

  logout(): void {
    this.idToken = undefined;
    this.msalService.logout({ postLogoutRedirectUri: "/" });
    this.userService.setLoggedUser();
  }

  private ensureAuthenticated(): Observable<AuthenticationResult | null> {
    const account = this.getCurrentAccount();

    // Avoid ssoSilent() because it always relies on a hidden iframe and can time out
    // in browsers/environments where third-party cookies or storage are restricted.
    if (!account) {
      return this.acquireTokenInteractively();
    }

    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: [environment.apiScope, "openid", "profile", "offline_access"]
      })
    ).pipe(
      catchError(error => {
        if (this.shouldFallbackToInteractive(error)) {
          console.warn("MSAL silent token acquisition requires interactive fallback.", {
            errorCode: (error as { errorCode?: string } | null)?.errorCode,
            message: (error as { message?: string; errorMessage?: string } | null)?.errorMessage ??
              (error as { message?: string; errorMessage?: string } | null)?.message
          });
          return this.acquireTokenInteractively();
        }
        throw error;
      })
    );
  }

  private acquireTokenInteractively(): Observable<AuthenticationResult | null> {
    const scopes = [environment.apiScope];

    const interactionType = this.msalGuardConfig?.interactionType ?? InteractionType.Redirect;
    if (interactionType === InteractionType.Popup) {
      return from(this.msalService.instance.acquireTokenPopup({ scopes }));
    }

    // Redirect does not return tokens to the current JS context (the page navigates away).
    return from(this.msalService.instance.acquireTokenRedirect({ scopes })).pipe(map(() => null));
  }

  private getCurrentAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0] ?? null;
  }

  private shouldFallbackToInteractive(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }

    const anyError = error as { errorCode?: string; message?: string; errorMessage?: string } | null;
    const errorCode = anyError?.errorCode ?? "";
    const message = (anyError?.errorMessage ?? anyError?.message ?? "").toLowerCase();

    // Some silent-acquisition failures are expected and should fall back to redirect/popup:
    // - token_refresh_required: no usable cached token, or a refresh/claims challenge is needed
    // - monitor_window_timeout / iframe timeout: hidden-iframe renewal could not complete
    return (
      errorCode === "token_refresh_required" ||
      errorCode === "monitor_window_timeout" ||
      message.includes("token_refresh_required") ||
      message.includes("token must be refreshed") ||
      message.includes("token acquisition in iframe") ||
      (message.includes("iframe") && message.includes("timeout"))
    );
  }
}
